import { useState } from 'react';
import {
  Alert,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';
import theme from '../../../theme/theme';
import { DetailCard } from '../DetailCard';
import type { TabbedCardProps, TabbedCardColumnDef } from './TabbedCard.types';

export type { TabbedCardProps, TabbedCardColumnDef };
export type { TabbedCardTabConfig } from './TabbedCard.types';

const PLACEHOLDER = '—';

// ── TabbedTable — pure table for a list of rows ───────────────────────────────

type TabbedTableProps<TRow> = {
  tabKey: string;
  rows: TRow[];
  columns: TabbedCardColumnDef<TRow>[];
  emptyLabel: string;
};

function TabbedTable<TRow>({ tabKey, rows, columns, emptyLabel }: TabbedTableProps<TRow>) {
  if (rows.length === 0) {
    return (
      <Alert
        severity="info"
        variant="outlined"
        sx={{ mt: 1, fontSize: theme.typography.body2.fontSize }}
      >
        {emptyLabel}
      </Alert>
    );
  }

  return (
    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 320 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
            {columns.map(col => (
              <TableCell
                key={col.key}
                sx={{
                  color: theme.palette.text.body,
                  fontWeight: theme.typography.fontWeightSemiBold,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  whiteSpace: 'nowrap',
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              key={`${tabKey}|${idx}|${columns.map(c => c.getValue(row)).join('|')}`}
              sx={{
                '&:last-child td': { borderBottom: 0 },
                '&:hover': { bgcolor: theme.palette.grey[50] },
              }}
            >
              {columns.map(col => {
                const value = col.getValue(row).trim();
                const isEmpty = value === '';
                return (
                  <TableCell
                    key={col.key}
                    sx={{
                      color: isEmpty ? theme.palette.text.label : theme.palette.text.body,
                      fontWeight: isEmpty
                        ? theme.typography.fontWeightRegular
                        : theme.typography.fontWeightMedium,
                      fontSize: theme.typography.body2.fontSize,
                    }}
                  >
                    {isEmpty ? PLACEHOLDER : col.renderCell ? col.renderCell(value) : value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── TabbedCard — agnostic presentation component ──────────────────────────────

/**
 * Presentational card displaying rows grouped by category in a tabbed table.
 *
 * Fully agnostic: all labels, visual tokens, tabs, columns, and data are
 * supplied by the caller via props. Can be reused for any entity and any
 * row data type (TRow).
 */
export function TabbedCard<TKey extends string, TRow>({
  title,
  icon,
  iconBg,
  tabs,
  columns,
  emptyLabel,
  tabData,
  stretch = false,
  collapsible,
  defaultExpanded,
}: TabbedCardProps<TKey, TRow>) {
  const [activeTab, setActiveTab] = useState<TKey>(tabs[0].key);

  return (
    <DetailCard
      icon={icon}
      iconBg={iconBg}
      title={title}
      stretch={stretch}
      collapsible={collapsible}
      defaultExpanded={defaultExpanded}
    >
      <Tabs
        value={activeTab}
        onChange={(_, value: TKey) => setActiveTab(value)}
        sx={{
          mb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 40,
          '& .MuiTab-root': {
            minHeight: 40,
            py: 1,
            textTransform: 'none',
            fontWeight: theme.typography.fontWeightSemiBold,
            fontSize: theme.typography.body2.fontSize,
          },
        }}
      >
        {tabs.map(tab => (
          <Tab key={tab.key} value={tab.key} label={tab.label} />
        ))}
      </Tabs>

      <TabbedTable
        tabKey={activeTab}
        rows={tabData[activeTab]}
        columns={columns}
        emptyLabel={emptyLabel}
      />
    </DetailCard>
  );
}
